import { useEffect, useRef } from 'react';
import {
  isAddressAutocompleteEnabled,
  loadGooglePlaces,
} from '../utils/googlePlaces';

/**
 * Attaches Google Places Autocomplete to an address input.
 * Falls back to a normal text field when no Maps API key is configured.
 */
export function usePlacesAddressAutocomplete(
  inputRef: React.RefObject<HTMLInputElement | null>,
  onSelect: (address: string) => void
): boolean {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const enabled = isAddressAutocompleteEnabled();

  useEffect(() => {
    if (!enabled) return;
    const input = inputRef.current;
    if (!input) return;

    let cancelled = false;
    let autocomplete: ReturnType<NonNullable<NonNullable<Window['google']>['maps']>['places']['Autocomplete']> | null = null;

    loadGooglePlaces()
      .then(() => {
        if (cancelled || !inputRef.current || !window.google?.maps?.places) return;
        autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['geocode'],
          componentRestrictions: { country: ['ca', 'us'] },
          fields: ['formatted_address', 'name'],
        });
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete?.getPlace();
          const address = place?.formatted_address || place?.name || '';
          if (address) {
            onSelectRef.current(address);
          }
        });
      })
      .catch((error) => {
        console.warn('Address autocomplete unavailable:', error);
      });

    return () => {
      cancelled = true;
      if (autocomplete && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [enabled, inputRef]);

  return enabled;
}
